import * as React from "react"
import {
  DirectionFromOrigin,
  useSummarizeTransactionsByTimeQuery,
} from "@digits-graphql/frontend/graphql-bearer"
import { DEFAULT_ELEMENTS_PER_SCROLLABLE_PAGE } from "@digits-shared/components/UI/Elements/HorizontalScrollChevron"
import dateTimeHelper from "@digits-shared/helpers/dateTimeHelper"
import useRouter from "@digits-shared/hooks/useRouter"
import { useAnalysisCategoryTypes } from "src/frontend/components/OS/Applications/Analysis/AnalysisCategoryTypesContext"
import { useViewVersion } from "src/frontend/components/Shared/Contexts/useViewVersion"
import { BarChartComponent } from "src/frontend/components/Shared/Layout/Components/Charts/BarChartComponent"
import {
  type TimeseriesValue,
  toSortedTimeseriesValues,
} from "src/frontend/components/Shared/Layout/Components/Charts/toTimeseries"
import { ComponentSize } from "src/frontend/components/Shared/Layout/ComponentSize"
import { useFrontendPathGenerator } from "src/frontend/hooks/useFrontendPathGenerator"
import routes from "src/frontend/routes"
import useIntervalOrigin from "src/shared/hooks/useIntervalOrigin"

const INTERVALS_DISPLAY = 12

export const RevenueBarChart: React.FC = () => {
  const viewKey = useViewVersion()
  const intervalOrigin = useIntervalOrigin()
  const generatePath = useFrontendPathGenerator()
  const categoryTypes = useAnalysisCategoryTypes()
  const { history } = useRouter()

  const { loading, data } = useSummarizeTransactionsByTimeQuery({
    variables: {
      origin: {
        ...intervalOrigin,
        intervalCount: DEFAULT_ELEMENTS_PER_SCROLLABLE_PAGE * 8,
      },
      direction: DirectionFromOrigin.Past,

      filter: {
        viewKey,
        categoryTypes,
      },
    },
  })

  const [selectedIndex, setSelectedIndex] = React.useState<number | undefined>(undefined)
  const onMouseOver = React.useCallback((value: TimeseriesValue, index: number) => {
    setSelectedIndex(index)
  }, [])
  const onMouseOut = React.useCallback((value?: TimeseriesValue) => {
    setSelectedIndex(undefined)
  }, [])

  const chartData: TimeseriesValue[] = React.useMemo(
    () =>
      toSortedTimeseriesValues(
        data?.dimensionalTransactionSummary.time.map((t) => t.summary)?.slice(0, INTERVALS_DISPLAY)
      ),
    [data?.dimensionalTransactionSummary.time]
  )

  const onClick = React.useCallback(
    ({ period }: TimeseriesValue, index: number) => {
      const periodIntervalOrigin = dateTimeHelper.intervalOriginFromPeriod(period)
      history.push(generatePath(routes.revenue, periodIntervalOrigin))
    },
    [generatePath, history]
  )

  return (
    <div css="width: 100%; margin-bottom: 20px">
      <BarChartComponent
        componentSize={ComponentSize.ExtraLarge}
        title="Revenue Over Time"
        icon={undefined}
        timeseries={chartData}
        loading={loading}
        skipAnimations={false}
        selectedIndex={selectedIndex}
        onMouseOver={onMouseOver}
        onMouseOut={onMouseOut}
        onClick={onClick}
        height={250}
      />
    </div>
  )
}
